import { Box, SxProps, TableCell, Theme } from '@mui/material'
import { Link, To, useNavigate } from 'react-router-dom'

import Tooltip, { TooltipProps } from 'components/Tooltip'
import { cellWrapperStyles } from '../cellWrapperStyles'
import { CellProps, LinkSyncProps } from './types'
import { useCell } from './useCell'
import { cellIndex } from './utils'

export const Cell = <T,>({
  cell,
  onClickRow,
  onClickRowAsync,
}: CellProps<T>): React.JSX.Element => {
  const { Content, isClickable, row, styles } = useCell({ cell })
  const navigate = useNavigate()

  const tooltipProps: TooltipProps | undefined = cell
    .getContext()
    .table.options.meta?.tooltipRow?.(row)

  const commonSX = ({ cursorPointer }: { cursorPointer?: boolean } = {}): SxProps<Theme> => ({
    ...cellWrapperStyles<T>(cell.column.columnDef.meta),
    ...(cursorPointer && { cursor: 'pointer' }),
  })

  const handleOnClickAsync = (): void => {
    if (onClickRowAsync) {
      Promise.resolve(onClickRowAsync(row.original)).then((to: To | void): void => {
        if (to) navigate(to)
      })
    }
  }

  const Default = (): React.JSX.Element => <Box sx={commonSX()}>{Content}</Box>

  const LinkSync = ({ onClickRow }: LinkSyncProps<T>): React.JSX.Element => {
    const to: To | undefined = onClickRow(row.original)

    if (to === undefined) {
      return Default()
    }

    return (
      <Box
        component={Link}
        sx={commonSX({ cursorPointer: true })}
        to={to}
      >
        {Content}
      </Box>
    )
  }

  const LinkAsync = (): React.JSX.Element => (
    <Box
      onClick={handleOnClickAsync}
      sx={commonSX({ cursorPointer: true })}
    >
      {Content}
    </Box>
  )

  const createContentWrapper = (): React.JSX.Element => {
    if (isClickable) {
      if (onClickRow !== undefined) {
        return LinkSync({ onClickRow })
      } else if (onClickRowAsync !== undefined) {
        return LinkAsync()
      }
    }

    return Default()
  }
  const ContentWrapper: React.JSX.Element = createContentWrapper()

  return (
    <TableCell
      data-cy={cellIndex<T>(cell)}
      key={cell.id}
      sx={styles}
    >
      {tooltipProps ? (
        <Tooltip
          flexBox={false}
          {...tooltipProps}
        >
          {ContentWrapper}
        </Tooltip>
      ) : (
        ContentWrapper
      )}
    </TableCell>
  )
}
